'use client'
import {
  Typography_default
} from "./chunk-6UL5SZLI.mjs";
import {
  __objRest,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/Input/Input.tsx
import { useId, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ExclamationIcon, EyeIcon } from "@wbk/svg";
import { jsx, jsxs } from "react/jsx-runtime";
var MODE = {
  number: "numeric",
  tel: "tel"
};
var Input = (_a) => {
  var _b = _a, {
    label,
    labelVariant = "default",
    placeholder,
    error,
    onChange,
    onFileChange,
    rounded = true,
    className,
    groupClassName,
    startIcon,
    endIcon,
    type,
    hideErrorSpace = true,
    hideErrorMark = false,
    securityLimit = 64,
    ref,
    id
  } = _b, props = __objRest(_b, [
    "label",
    "labelVariant",
    "placeholder",
    "error",
    "onChange",
    "onFileChange",
    "rounded",
    "className",
    "groupClassName",
    "startIcon",
    "endIcon",
    "type",
    "hideErrorSpace",
    "hideErrorMark",
    "securityLimit",
    "ref",
    "id"
  ]);
  const [view, setView] = useState(false);
  const backUpId = useId();
  const inputId = id || backUpId;
  return /* @__PURE__ */ jsxs("div", { className: twMerge("space-y-1", groupClassName), children: [
    labelVariant === "default" && label && /* @__PURE__ */ jsx(
      Typography_default,
      {
        as: "label",
        variant: "label-M",
        className: "aria-disabled:text-text-disabled block",
        "aria-disabled": props.disabled,
        htmlFor: inputId,
        children: label
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      startIcon && /* @__PURE__ */ jsx("div", { className: "absolute inset-y-0 h-full w-full max-w-[40px]", children: startIcon }),
      /* @__PURE__ */ jsx(
        "input",
        __spreadValues({
          id: inputId,
          placeholder: placeholder || " ",
          className: twMerge(
            "bg-input hover:bg-input-hover focus:bg-input-hover placeholder:text-text-tertiary p-XS peer w-full border focus:outline-none focus:ring-0",
            "disabled:bg-input-disabled disabled:text-text-disabled focus:border-input-border-focus disabled:cursor-not-allowed",
            error ? "border-error" : "border-input-border",
            rounded ? "rounded-S" : "rounded-none",
            props.dir === "ltr" ? "pr-XS" : "ltr:pr-XS rtl:pl-XS rtl:text-right",
            startIcon ? "ps-8" : "",
            endIcon ? "pe-8" : "",
            label && labelVariant === "floating" ? "pt-[calc(var(--spacing-XS)*1.5)]" : "",
            className
          ),
          ref,
          onChange: (e) => {
            var _a2;
            if (type === "file") {
              return onFileChange == null ? void 0 : onFileChange(e.target.files);
            }
            onChange == null ? void 0 : onChange((_a2 = e.target.value) == null ? void 0 : _a2.slice(0, securityLimit));
          },
          type: type === "password" ? view ? "text" : "password" : type,
          inputMode: MODE[type || ""] || props.inputMode
        }, props)
      ),
      labelVariant === "floating" && label && /* @__PURE__ */ jsx(
        Typography_default,
        {
          as: "label",
          color: "text-secondary",
          className: "aria-disabled:text-text-disabled -translate-y-[calc(25%_-_var(--spacing-XS)] ltr:left-XXS rtl:right-XXS absolute top-[calc(var(--spacing-XS)*0.65)] z-10 origin-top-left scale-75 ps-[calc(var(--spacing-XS)*0.75)] transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-[calc(var(--spacing-XS)*0.65)] peer-focus:translate-y-0 peer-focus:scale-75 data-[dir=rtl]:left-auto data-[icon=true]:ps-8 rtl:origin-top-right",
          dir: props.dir,
          "data-dir": props.dir,
          "data-icon": !!startIcon,
          "aria-disabled": props.disabled,
          htmlFor: inputId,
          children: label
        }
      ),
      !!error && !hideErrorMark && /* @__PURE__ */ jsx(
        ExclamationIcon,
        {
          "data-dir": props.dir,
          "data-type": type,
          className: "text-error mx-XS absolute inset-y-0 h-full data-[dir=ltr]:right-0 data-[dir=rtl]:left-0 data-[type=date]:right-6 ltr:right-0 rtl:left-0"
        }
      ),
      type === "password" && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: `text-text-secondary absolute inset-y-0 mx-1 h-full text-xs hover:opacity-70 focus:opacity-70 ${error ? "ltr:right-6 rtl:left-6" : "ltr:right-0 rtl:left-0"}`,
          onClick: (e) => {
            e.preventDefault();
            setView((prev) => !prev);
          },
          tabIndex: -1,
          children: /* @__PURE__ */ jsx(
            EyeIcon,
            {
              "data-view": view,
              className: "mx-XXS data-[view=true]:text-text-disabled h-5 w-5"
            }
          )
        }
      ),
      endIcon && /* @__PURE__ */ jsx("div", { className: "absolute inset-y-0 h-full ltr:right-0 rtl:left-0", children: endIcon })
    ] }),
    (typeof error === "string" && !!error || !hideErrorSpace) && /* @__PURE__ */ jsx(
      Typography_default,
      {
        color: "error",
        variant: "body-XS",
        className: "invisible min-h-4 data-[error=true]:visible data-[dir=ltr]:text-left data-[dir=rtl]:text-right",
        "data-dir": props.dir,
        "data-error": !!error,
        children: error
      }
    )
  ] });
};
var Input_default = Input;

// src/Input/TextArea.tsx
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TextArea = (_a) => {
  var _b = _a, {
    label,
    error,
    onChange,
    rounded = true,
    className,
    groupClassName,
    hideErrorSpace = true,
    hideErrorMark = false,
    securityLimit = 640,
    ref
  } = _b, props = __objRest(_b, [
    "label",
    "error",
    "onChange",
    "rounded",
    "className",
    "groupClassName",
    "hideErrorSpace",
    "hideErrorMark",
    "securityLimit",
    "ref"
  ]);
  return /* @__PURE__ */ jsxs2("div", { className: groupClassName, children: [
    label && /* @__PURE__ */ jsx2(
      Typography_default,
      {
        as: "label",
        variant: "label-M",
        className: "aria-disabled:text-text-disabled block pb-1",
        "aria-disabled": props.disabled,
        htmlFor: props.id,
        children: label
      }
    ),
    /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
      /* @__PURE__ */ jsx2(
        "textarea",
        __spreadValues({
          className: twMerge2(
            "bg-input hover:bg-input-hover focus:bg-input-hover placeholder:text-text-tertiary w-full border p-2 focus:outline-none focus:ring-0",
            "disabled:bg-input-disabled disabled:text-text-disabled focus:border-input-border-focus disabled:cursor-not-allowed",
            error ? "border-error" : "border-input-border",
            rounded ? "rounded-S" : "rounded-none",
            props.dir === "ltr" ? "pr-6" : "ltr:pr-6 rtl:pl-6 rtl:text-right",
            className
          ),
          ref,
          onChange: (e) => {
            var _a2;
            onChange == null ? void 0 : onChange((_a2 = e.target.value) == null ? void 0 : _a2.slice(0, securityLimit));
          }
        }, props)
      ),
      !!error && !hideErrorMark && /* @__PURE__ */ jsx2(
        "svg",
        {
          width: "18",
          height: "18",
          viewBox: "0 0 20 19",
          "data-dir": props.dir,
          className: `fill-error absolute inset-y-0 right-0 mx-1 h-full ${props.dir === "ltr" ? "" : "rtl:left-0 rtl:right-auto"}`,
          children: /* @__PURE__ */ jsx2("path", { d: "M9.99121 18.7422C14.9746 18.7422 19.0879 14.6289 19.0879 9.6543C19.0879 4.67969 14.9658 0.566406 9.98242 0.566406C5.00781 0.566406 0.90332 4.67969 0.90332 9.6543C0.90332 14.6289 5.0166 18.7422 9.99121 18.7422ZM9.99121 16.9316C5.95703 16.9316 2.73145 13.6885 2.73145 9.6543C2.73145 5.62012 5.95703 2.38574 9.98242 2.38574C14.0166 2.38574 17.2598 5.62012 17.2686 9.6543C17.2773 13.6885 14.0254 16.9316 9.99121 16.9316ZM9.98242 11.1133C10.4658 11.1133 10.7471 10.8408 10.7559 10.3311L10.8877 6.10352C10.9053 5.58496 10.5186 5.20703 9.97363 5.20703C9.42871 5.20703 9.05078 5.57617 9.06836 6.09473L9.19141 10.3311C9.20898 10.832 9.49023 11.1133 9.98242 11.1133ZM9.98242 14.0312C10.5537 14.0312 11.0195 13.6182 11.0195 13.0557C11.0195 12.502 10.5625 12.0889 9.98242 12.0889C9.41113 12.0889 8.94531 12.502 8.94531 13.0557C8.94531 13.6094 9.41992 14.0312 9.98242 14.0312Z" })
        }
      )
    ] }),
    (typeof error === "string" && !!error || !hideErrorSpace) && /* @__PURE__ */ jsx2(
      "p",
      {
        "data-visible": !!error,
        className: "text-error my-1 min-h-[16px] text-xs data-[visible=false]:text-transparent",
        children: error
      }
    )
  ] });
};
var TextArea_default = TextArea;

export {
  Input_default,
  TextArea_default
};
//# sourceMappingURL=chunk-OCLOL5BZ.mjs.map